import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

export default function TransferReport() {
  const [range, setRange, apiParams] = useRangeState('last_7');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'transfers', merged],
    queryFn: () => reportsApi.transfers(merged),
    keepPreviousData: true,
  });

  const perQueue = data?.per_queue || [];
  const perDay = data?.per_day || [];

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['queue']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Transfer report</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="transfers" params={merged} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      {data?.meta?.note && (
        <div className="text-xs text-surface-muted mb-2">{data.meta.note}</div>
      )}

      {perDay.length > 0 && (
        <div className="mb-4 rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel">
          <h3 className="text-sm font-semibold mb-2">Daily transfers</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perDay} margin={{ top: 8, right: 20, bottom: 24, left: 0 }}>
                <CartesianGrid stroke="rgba(120,120,120,0.15)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-20} dy={10} height={44} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="transfers" fill="#2196F3" name="Transfers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <ReportTable
        headers={['queue_id','queue_name','transfers','answered','transfer_rate_pct']}
        rows={perQueue}
        columnLabels={{
          queue_id: 'Queue ID', queue_name: 'Queue',
          transfers: 'Transfers', answered: 'Answered',
          transfer_rate_pct: 'Transfer rate',
        }}
        formatters={{ transfer_rate_pct: (v) => v == null ? '—' : `${v}%` }}
      />
    </div>
  );
}
